import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  SlidersHorizontal, Fuel, Truck, Package, RotateCcw,
  Info, Calendar, FileText,
} from 'lucide-react';
import { fetchContractEvalData } from '../services/api';

const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = n => `${(Math.round((Number(n) || 0) * 10) / 10).toFixed(1)}%`;
// DATE columns serialize at UTC midnight → render the prior day in Eastern. Parse local.
const fmtDate = v => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const clampPct = v => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

export default function ContractEvaluation() {
  const [scopeMode, setScopeMode] = useState('all');     // 'all' | 'invoice' | 'range'
  const [invoiceNo, setInvoiceNo] = useState('');
  const [range, setRange] = useState({ start: '', end: '' });
  const [invoiceOptions, setInvoiceOptions] = useState([]); // populated from first 'all' load
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // scenario state — keyed by service / accessorial code → new discount %
  const [transPct, setTransPct] = useState({});   // { service: newPct }
  const [accPct, setAccPct] = useState({});        // { code|desc: newPct }
  const [fuelOff, setFuelOff] = useState(0);       // extra % off fuel surcharge

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const scope =
        scopeMode === 'invoice' && invoiceNo ? { invoice_number: invoiceNo }
        : scopeMode === 'range' ? { start: range.start || undefined, end: range.end || undefined }
        : {};
      const d = await fetchContractEvalData(scope);
      setData(d);
      // seed scenario at current (realized) discounts = no change
      const tp = {}; (d?.transportation || []).forEach(t => { tp[t.service] = t.discount_pct; });
      const ap = {}; (d?.accessorials || []).forEach(x => { ap[x.key] = x.discount_pct; });
      setTransPct(tp); setAccPct(ap); setFuelOff(0);
      // keep the invoice dropdown populated from whatever scope returned
      if (d?.scope?.invoices?.length && (!invoiceOptions.length || scopeMode === 'all')) {
        setInvoiceOptions(d.scope.invoices);
      }
    } catch (e) {
      setError(e.message || 'Failed to load bill data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [scopeMode, invoiceNo, range.start, range.end]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── live what-if math ──────────────────────────────────────────────────────
  const model = useMemo(() => {
    if (!data) return null;
    const trans = data.transportation || [];
    const acc = data.accessorials || [];
    const fuelNet = data.fuel?.net || 0;
    const fuelAttr = data.fuel?.attributed || 0;
    const unattrFuel = Math.max(0, fuelNet - fuelAttr);

    // Anchor on the ACTUAL net + gross and the DELTA in points off gross. "+2 pts"
    // = save 2% of gross, regardless of the (display-rounded) current discount —
    // so an untouched lever is exactly $0 (no phantom savings from rounding).
    let transportSaving = 0, sumNewFuelAttr = 0;
    const transRows = trans.map(t => {
      const newPct = clampPct(transPct[t.service] ?? t.discount_pct);
      const delta = newPct - t.discount_pct;
      let saving = t.gross * delta / 100;
      let newNet = t.net - saving;
      if (newPct >= 100) { newNet = 0; saving = t.net; } // waived / 100% off
      const fuelRate = t.net > 0 ? t.fuel_net / t.net : 0;
      const newFuelAttr = fuelRate * newNet;
      transportSaving += saving;
      sumNewFuelAttr += newFuelAttr;
      return {
        ...t, newPct, newNet, transportSaving: saving,
        fuelSaving: t.fuel_net - newFuelAttr,
        changed: Math.abs(delta) > 0.001,
      };
    });

    // accessorials — same delta-anchored math; each carries the fuel that rides
    // on it, so discounting/waiving one also trims its fuel (penny-exact).
    let accSaving = 0, sumNewFuelAcc = 0;
    const accRows = acc.map(x => {
      const newPct = clampPct(accPct[x.key] ?? x.discount_pct);
      const delta = newPct - x.discount_pct;
      let saving = x.gross * delta / 100;
      let newNet = x.net - saving;
      if (newPct >= 100) { newNet = 0; saving = x.net; } // waived
      const fuelRate = x.net > 0 ? x.fuel_net / x.net : 0;
      const newFuelAcc = fuelRate * newNet;
      accSaving += saving;
      sumNewFuelAcc += newFuelAcc;
      return {
        ...x, newPct, newNet, saving,
        fuelSaving: x.fuel_net - newFuelAcc,
        changed: Math.abs(delta) > 0.001,
      };
    });

    // fuel: transport + accessorial discounts shrink the fuel riding on them
    // (knock-on), then an explicit "% off fuel" lever applies to what's left.
    const fuelAfter = sumNewFuelAttr + sumNewFuelAcc + unattrFuel;
    const knockOnSaving = fuelNet - fuelAfter;
    const explicitFuelSaving = fuelAfter * (clampPct(fuelOff) / 100);
    const fuelSaving = knockOnSaving + explicitFuelSaving;

    const totalSaving = transportSaving + fuelSaving + accSaving;
    const billed = data.scope?.billed_total || 0;
    const count = data.scope?.count || 0;
    return {
      transRows, accRows,
      transportSaving, fuelSaving, knockOnSaving, explicitFuelSaving, accSaving,
      totalSaving, billed, modeled: billed - totalSaving,
      pctOfBill: billed > 0 ? totalSaving / billed * 100 : 0,
      annual: count > 0 ? totalSaving / count * 52 : 0,
      count, fuelNet,
    };
  }, [data, transPct, accPct, fuelOff]);

  const dirty = useMemo(() => {
    if (!model) return false;
    return Math.abs(model.totalSaving) > 0.005;
  }, [model]);

  const reset = () => {
    if (!data) return;
    const tp = {}; (data.transportation || []).forEach(t => { tp[t.service] = t.discount_pct; });
    const ap = {}; (data.accessorials || []).forEach(x => { ap[x.key] = x.discount_pct; });
    setTransPct(tp); setAccPct(ap); setFuelOff(0);
  };

  // Set every lever that has a known contract discount to that contract value —
  // models "what if UPS honored the full contract terms?" in one click.
  const setToContract = () => {
    if (!data) return;
    setTransPct(p => { const n = { ...p }; (data.transportation || []).forEach(t => { if (t.contract_pct != null) n[t.service] = t.contract_pct; }); return n; });
    setAccPct(p => { const n = { ...p }; (data.accessorials || []).forEach(x => { if (x.contract_pct != null) n[x.key] = x.contract_pct; }); return n; });
  };
  const hasContract = !!data && [...(data.transportation || []), ...(data.accessorials || [])].some(r => r.contract_pct != null);

  const bumpAcc = (key, delta) =>
    setAccPct(p => ({ ...p, [key]: clampPct((Number(p[key]) || 0) + delta) }));

  const bump = (service, delta) =>
    setTransPct(p => ({ ...p, [service]: clampPct((Number(p[service]) || 0) + delta) }));

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="page-container">
      <div style={{ marginBottom: 4, fontSize: 13, color: 'var(--gray-500)' }}>
        UPS <span style={{ margin: '0 6px' }}>›</span> Contract Evaluation
      </div>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 4px' }}>
        <SlidersHorizontal size={26} style={{ color: 'var(--orange)' }} /> Contract Evaluation
      </h1>
      <p style={{ color: 'var(--gray-500)', marginTop: 0, maxWidth: 760 }}>
        Model your real UPS bills against any contract variable. Change a service discount, the fuel
        surcharge, or an accessorial rate and instantly see what the bill <em>would</em> have been —
        for one invoice or across a stretch of time. Fuel surcharge moves automatically with transport,
        the way UPS actually bills it.
      </p>

      {/* ── scope selector ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['all', 'All bills'], ['invoice', 'One invoice'], ['range', 'Date range']].map(([k, label]) => (
              <button key={k}
                className={scopeMode === k ? 'btn btn-primary' : 'btn btn-outline'}
                onClick={() => setScopeMode(k)} style={{ padding: '6px 14px' }}>
                {label}
              </button>
            ))}
          </div>
          {scopeMode === 'invoice' && (
            <select className="form-select" style={{ maxWidth: 320 }}
              value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)}>
              <option value="">Select an invoice…</option>
              {invoiceOptions.map(iv => (
                <option key={iv.invoice_number} value={iv.invoice_number}>
                  {iv.invoice_number} — {fmtDate(iv.invoice_date)} — {fmt(iv.invoice_amount)}
                </option>
              ))}
            </select>
          )}
          {scopeMode === 'range' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Calendar size={16} style={{ color: 'var(--gray-500)' }} />
              <input type="date" className="form-input" value={range.start}
                onChange={e => setRange(r => ({ ...r, start: e.target.value }))} />
              <span style={{ color: 'var(--gray-500)' }}>to</span>
              <input type="date" className="form-input" value={range.end}
                onChange={e => setRange(r => ({ ...r, end: e.target.value }))} />
            </div>
          )}
        </div>
      </div>

      {loading && <div className="card"><div className="card-body">Loading bill data…</div></div>}
      {error && !loading && (
        <div className="card"><div className="card-body" style={{ color: 'var(--orange)' }}>
          {error}
        </div></div>
      )}
      {!loading && !error && data && data.scope.count === 0 && (
        <div className="card"><div className="card-body">
          No imported bills in this scope. Import a UPS bill on the <strong>Bills</strong> page first.
        </div></div>
      )}

      {!loading && !error && model && data.scope.count > 0 && (
        <>
          {/* ── results headline ── */}
          <div className="card" style={{ marginBottom: 16, borderTop: '3px solid var(--orange)' }}>
            <div className="card-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                  {data.scope.count} bill{data.scope.count !== 1 ? 's' : ''} in scope
                  {data.scope.start && <> · {fmtDate(data.scope.start)} → {fmtDate(data.scope.end)}</>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {hasContract && (
                    <button className="btn btn-outline" onClick={setToContract} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px' }}
                      title="Set every lever to its negotiated contract discount">
                      <FileText size={15} /> Model full contract
                    </button>
                  )}
                  {dirty && (
                    <button className="btn btn-ghost" onClick={reset} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <RotateCcw size={15} /> Reset
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 12 }}>
                <Metric label="Billed now" value={fmt(model.billed)} sub="what UPS actually charged" />
                <Metric label="Modeled bill" value={fmt(model.modeled)}
                  color={dirty ? 'var(--navy)' : undefined} sub="under this scenario" />
                <Metric label="You'd save" value={fmt(model.totalSaving)}
                  color={model.totalSaving > 0.005 ? '#1a8a4a' : model.totalSaving < -0.005 ? '#c62828' : undefined}
                  sub={dirty ? `${pct(model.pctOfBill)} of the bill` : 'adjust a lever below'} big />
                <Metric label="≈ Per year" value={fmt(model.annual)}
                  color={model.annual > 0.005 ? '#1a8a4a' : undefined}
                  sub="if applied to ~52 weekly bills" />
              </div>
              {dirty && (
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--gray-600)', display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                  <span><Truck size={13} style={{ verticalAlign: -2 }} /> Transport <strong>{fmt(model.transportSaving)}</strong></span>
                  <span><Fuel size={13} style={{ verticalAlign: -2 }} /> Fuel <strong>{fmt(model.fuelSaving)}</strong>
                    {model.explicitFuelSaving > 0.005 && <span style={{ color: 'var(--gray-400)' }}> (incl. {fmt(model.knockOnSaving)} from transport)</span>}</span>
                  {Math.abs(model.accSaving) > 0.005 && <span><Package size={13} style={{ verticalAlign: -2 }} /> Accessorials <strong>{fmt(model.accSaving)}</strong></span>}
                </div>
              )}
            </div>
          </div>

          {/* ── transportation levers ── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Truck size={18} style={{ color: 'var(--navy)' }} />
              <strong>Transportation services</strong>
              <span style={{ color: 'var(--gray-500)', fontWeight: 400, fontSize: 13 }}>
                — set a new discount off the gross (list) rate
              </span>
            </div>
            <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
              <table className="orders-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Service</th>
                    <th style={{ textAlign: 'right' }}>Pkgs</th>
                    <th style={{ textAlign: 'right' }}>Gross (list)</th>
                    <th style={{ textAlign: 'right' }}>Now paying</th>
                    <th style={{ textAlign: 'center' }}>Current %</th>
                    <th style={{ textAlign: 'center' }} title="Negotiated contract discount">Contract %</th>
                    <th style={{ textAlign: 'center', minWidth: 200 }}>New discount %</th>
                    <th style={{ textAlign: 'right' }}>Modeled</th>
                    <th style={{ textAlign: 'right' }}>Saving</th>
                  </tr>
                </thead>
                <tbody>
                  {model.transRows.map(t => (
                    <tr key={t.service} style={t.changed ? { background: '#f0f7ff' } : undefined}>
                      <td style={{ fontWeight: 600 }}>{t.service}
                        {t.fuel_net > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 400 }}>
                            +{fmt(t.fuel_net)} fuel rides on this
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>{t.packages}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(t.gross)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(t.net)}</td>
                      <td style={{ textAlign: 'center', color: 'var(--gray-500)' }}>{pct(t.discount_pct)}</td>
                      <ContractCell contract={t.contract_pct} current={t.discount_pct} />
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <button className="btn btn-ghost" style={stepBtn} onClick={() => bump(t.service, -1)} title="−1 point">−</button>
                          <input type="number" className="form-input" step="0.5" min="0" max="100"
                            style={{ width: 72, textAlign: 'right' }}
                            value={t.newPct}
                            onChange={e => setTransPct(p => ({ ...p, [t.service]: e.target.value }))} />
                          <span style={{ color: 'var(--gray-400)' }}>%</span>
                          <button className="btn btn-ghost" style={stepBtn} onClick={() => bump(t.service, +1)} title="+1 point">+</button>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt(t.newNet)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600,
                        color: (t.transportSaving + t.fuelSaving) > 0.005 ? '#1a8a4a'
                          : (t.transportSaving + t.fuelSaving) < -0.005 ? '#c62828' : 'var(--gray-400)' }}>
                        {fmt(t.transportSaving + t.fuelSaving)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── fuel lever ── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Fuel size={18} style={{ color: '#b8860b' }} />
              <strong>Fuel surcharge</strong>
            </div>
            <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 24 }}>
              <div style={{ fontSize: 13, color: 'var(--gray-600)', maxWidth: 420 }}>
                <Info size={13} style={{ verticalAlign: -2, color: 'var(--gray-400)' }} /> Total fuel surcharge in
                scope: <strong>{fmt(model.fuelNet)}</strong>. Transport discounts above already trim fuel automatically.
                Use this for an <em>extra</em> fuel concession (e.g. "UPS gives us 2% off fuel").
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13 }}>Additional off fuel:</span>
                {[0, 1, 2, 5].map(v => (
                  <button key={v} className={fuelOff === v ? 'btn btn-primary' : 'btn btn-outline'}
                    style={qbtn} onClick={() => setFuelOff(v)}>{v}%</button>
                ))}
                <input type="number" className="form-input" step="0.5" min="0" max="100"
                  style={{ width: 78, textAlign: 'right' }} value={fuelOff}
                  onChange={e => setFuelOff(clampPct(e.target.value))} />
                <span style={{ color: 'var(--gray-400)' }}>%</span>
              </div>
            </div>
          </div>

          {/* ── accessorials (always shown — just as important as the service discounts) ── */}
          {model.accRows.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Package size={18} style={{ color: 'var(--orange)' }} />
                <strong>Accessorial charges</strong>
                <span style={{ color: 'var(--gray-500)', fontWeight: 400, fontSize: 13 }}>
                  — {model.accRows.length} types · set a new discount or Waive (fuel on each follows)
                </span>
              </div>
              <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
                <table className="orders-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Accessorial</th>
                      <th style={{ textAlign: 'right' }}>Gross</th>
                      <th style={{ textAlign: 'right' }}>Now paying</th>
                      <th style={{ textAlign: 'center' }}>Current %</th>
                      <th style={{ textAlign: 'center' }} title="Negotiated contract discount">Contract %</th>
                      <th style={{ textAlign: 'center', minWidth: 200 }}>New discount %</th>
                      <th style={{ textAlign: 'right' }}>Modeled</th>
                      <th style={{ textAlign: 'right' }}>Saving</th>
                    </tr>
                  </thead>
                  <tbody>
                    {model.accRows.map(x => (
                      <tr key={x.key} style={x.changed ? { background: '#fff6ee' } : undefined}>
                        <td style={{ fontWeight: 600 }}>{x.description}
                          {x.code && <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}> · {x.code}</span>}
                          {x.fuel_net > 0 && (
                            <div style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 400 }}>
                              +{fmt(x.fuel_net)} fuel rides on this
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmt(x.gross)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(x.net)}</td>
                        <td style={{ textAlign: 'center', color: 'var(--gray-500)' }}>{pct(x.discount_pct)}</td>
                        <ContractCell contract={x.contract_pct} current={x.discount_pct} />
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <button className="btn btn-ghost" style={stepBtn} onClick={() => bumpAcc(x.key, -1)} title="−1 point">−</button>
                            <input type="number" className="form-input" step="1" min="0" max="100"
                              style={{ width: 72, textAlign: 'right' }} value={x.newPct}
                              onChange={e => setAccPct(p => ({ ...p, [x.key]: e.target.value }))} />
                            <span style={{ color: 'var(--gray-400)' }}>%</span>
                            <button className="btn btn-ghost" style={stepBtn} onClick={() => bumpAcc(x.key, +1)} title="+1 point">+</button>
                            <button className="btn btn-ghost" style={{ ...qbtn, marginLeft: 4 }}
                              onClick={() => setAccPct(p => ({ ...p, [x.key]: 100 }))} title="Waive entirely">Waive</button>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmt(x.newNet)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600,
                          color: (x.saving + x.fuelSaving) > 0.005 ? '#1a8a4a'
                            : (x.saving + x.fuelSaving) < -0.005 ? '#c62828' : 'var(--gray-400)' }}>
                          {fmt(x.saving + x.fuelSaving)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ fontSize: 12, color: 'var(--gray-400)', maxWidth: 820, marginBottom: 24 }}>
            <Info size={12} style={{ verticalAlign: -1 }} /> Modeled to the penny off what UPS actually billed —
            gross (list) rate = the discount already given + what we paid. Fuel is allocated to each charge it
            actually rides on (transport + most surcharges; declared value doesn't bear fuel), so discounting a
            service <em>or</em> an accessorial trims its fuel correctly. A row's Saving is all-in (charge + its
            fuel); the headline Fuel figure is the total fuel saved. <strong>Contract %</strong> is the negotiated
            rate from the Contract Pricing page (orange ▾ = we realized less than contract — worth a call to UPS);
            "Model full contract" sets every mapped lever to its contract value. Shipping-charge corrections{data.corrections_net ? ` (${fmt(data.corrections_net)} in scope)` : ''} are
            excluded from the levers. Annual figure assumes ~52 weekly bills.
          </div>
        </>
      )}
    </div>
  );
}

const qbtn = { padding: '2px 8px', fontSize: 12, minWidth: 0 };
const stepBtn = { padding: '1px 9px', fontSize: 17, lineHeight: 1.1, minWidth: 0, fontWeight: 700, border: '1px solid var(--gray-200)' };

// Contract reference cell — shows the negotiated discount and flags when what we
// actually realized is meaningfully below it (UPS not honoring the full term).
function ContractCell({ contract, current }) {
  if (contract == null) return <td style={{ textAlign: 'center', color: 'var(--gray-300)' }}>—</td>;
  const below = current < contract - 0.5;
  return (
    <td style={{ textAlign: 'center', fontWeight: 600, color: below ? 'var(--orange)' : 'var(--gray-600)' }}
      title={below ? `Realized ${pct(current)} — ${(contract - current).toFixed(1)} pts below contract` : 'At/above contract'}>
      {pct(contract)}{below ? ' ▾' : ''}
    </td>
  );
}

function Metric({ label, value, sub, color, big }) {
  return (
    <div>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-500)' }}>{label}</div>
      <div style={{ fontSize: big ? 30 : 24, fontWeight: 700, color: color || 'var(--navy)', lineHeight: 1.15 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{sub}</div>}
    </div>
  );
}
